import React, { useState, useEffect } from "react";
import styles from "./Footer.module.css";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { handleLinkClick } from "../../Config/helper";
import { fetchMegamenuData } from "../../services/CmsService";
import {
  getLocaleFromPath,
  getLocaleFromCountry,
  ensureLocalesLoaded,
} from "@/Config/countryConstants";
import { getSelectedCountryByUser } from "@/utils/cookieutils";

interface FooterItem {
  url: string;
  label: string;
}
interface SocialLink {
  id: number;
  link: {
    id: number;
    label: string;
    url: string;
  };
  image: {
    id: number;
    documentId: string;
    url: string;
    alternativeText: string | null;
  };
}

type FooterProps = {
  data: any;
  headerData: any;
};

const Footer: React.FC<FooterProps> = ({ data, headerData }) => {
  const router = useRouter();
  const { logo } = headerData || {};
  const [megamenuData, setMegamenuData] = useState<any>(null);
  const [selectedCountryCookie, setSelectedCountryCookie] = useState<
    string | null
  >(null);

  // Fetch megamenu data when component mounts or route changes
  useEffect(() => {
    const fetchMegamenu = async () => {
      try {
        // Ensure locales are loaded before determining locale
        await ensureLocalesLoaded();

        // Fetch locale-specific common-page for footer megamenu
        const cleanPath = (router.asPath || "/").split("?")[0].split("#")[0];
        const pathLocale = getLocaleFromPath(cleanPath);

        // Use cookie-aware locale selection (same logic as navigation)
        const cookieCountry = getSelectedCountryByUser();
        setSelectedCountryCookie(cookieCountry); // Update state for dependency tracking
        const effectiveLocale =
          cookieCountry && cookieCountry !== "south-africa"
            ? getLocaleFromCountry(cookieCountry)
            : pathLocale;

        const apiData = await fetchMegamenuData(effectiveLocale);
        const responseData = apiData?.data || apiData;

        // Check if the data has the expected structure with mega_menu_header
        const hasExpectedStructure =
          responseData?.mega_menu_header?.main_nav_links &&
          Array.isArray(responseData?.mega_menu_header?.main_nav_links);

        if (hasExpectedStructure) {
          setMegamenuData(responseData?.mega_menu_header);
        } else {
          setMegamenuData(null);
        }
      } catch (error) {
        console.error("Error fetching megamenu data:", error);
        setMegamenuData(null);
      }
    };

    fetchMegamenu();
  }, [router.asPath, selectedCountryCookie]); // Re-run when route or cookie changes

  return (
    <footer className={styles.footer} data-section="footer">
      <div className="container">
        <div className="row">
          <div className="col-md-5 col-lg-4 pe-4">
            <div className={styles.logoSection}>
              <Link
                href={
                  (data as any)?.logo_url ||
                  (headerData as any)?.logo_url ||
                  logo?.link ||
                  "/"
                }
                aria-label="Logo"
                title={logo?.alt}
              >
                <Image
                  src={logo?.url || logo?.src}
                  alt={logo?.alternativeText || logo?.alt || "Logo"}
                  title={logo?.alternativeText || logo?.alt || "Logo"}
                  className={styles.logo}
                  width={204}
                  height={40}
                  loading="lazy"
                />
              </Link>
              <p className={styles.description}>
                {data?.transforming_way_text}
              </p>

              <div className={styles.socialLinks}>
                {data?.social_links?.map((link: SocialLink, index: number) => (
                  <Link
                    key={index}
                    href={link?.link?.url || "#"}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={
                      link?.link?.label || "Regenesys social-media-links"
                    }
                    title={link?.link?.label || "Regenesys social-media-links"}
                  >
                    <Image
                      src={link?.image?.url}
                      alt={
                        link?.image?.alternativeText ||
                        "Regenesys social-media-links"
                      }
                      title={
                        link?.image?.alternativeText ||
                        "Regenesys social-media-links"
                      }
                      width={24}
                      height={24}
                      priority
                    />
                  </Link>
                ))}
              </div>

              <div className={styles.contact}>
                <p>
                  Contact:
                  <Link
                    title={data?.contact?.email}
                    href={`mailto:${data?.contact?.email}` || "#"}
                  >
                    {data?.contact?.email}
                  </Link>
                  <span>|</span>
                  <span>{data?.contact?.phone_no}</span>
                </p>
              </div>
            </div>
          </div>

          <div className={`col-md-7 col-lg-8 ${styles.footerHeadingText}`}>
            <div className="row">
              {data?.footer_list?.map((footerSection: any, index: number) => (
                <div className="col-md-3" key={index}>
                  <p>{footerSection?.title}</p>
                  <ul className={styles.list_links}>
                    {footerSection?.items?.map(
                      (item: FooterItem, idx: number) => (
                        <li key={idx}>
                          {item?.url && (
                            <Link
                              href={item?.url || "#"}
                              aria-label={item?.label || "footer"}
                              title={item?.label}
                              onClick={(e) =>
                                handleLinkClick(e, item?.url || "#", router)
                              }
                            >
                              {item?.label}
                            </Link>
                          )}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.secondFooter}>
          <div className="row">
            <div className="col-md-12">
              <div className={styles.footerItems}>
                {megamenuData?.main_nav_links?.length > 0 && (
                  <div className={styles.submenuItems}>
                    <div className={styles.megamenuGrid}>
                      {/* First Column - First 3 schools (Law School, Economics, Technology) */}
                      <div className={styles.megamenuColumn}>
                        {megamenuData?.main_nav_links
                          ?.slice(0, 3)
                          ?.filter(
                            (subItem: any) =>
                              subItem?.title?.toLowerCase()?.trim() !==
                              "school of education"
                          )
                          ?.map((subItem: any) => {
                            return (
                              <div
                                key={subItem?.id}
                                className={styles.megamenuCategory}
                              >
                                {subItem?.url ? (
                                  <Link
                                    href={subItem?.url || "#"}
                                    className={styles.categoryTitle}
                                    title={subItem?.title}
                                  >
                                    <h3>{subItem?.title}</h3>
                                  </Link>
                                ) : (
                                  <h3 className={styles.categoryTitle}>
                                    {subItem?.title}
                                  </h3>
                                )}

                                {/* Handle sections (new API structure) */}
                                {subItem?.sections?.map(
                                  (section: any, i: number) => {
                                    return (
                                      <div
                                        key={i}
                                        className={styles.megamenuSection}
                                      >
                                        {section?.title && (
                                          <Link
                                            href={section?.url || ""}
                                            className={styles.sectionTitle}
                                            title={section?.title}
                                          >
                                            <h4>{section?.title}</h4>
                                          </Link>
                                        )}
                                        {section?.items?.map(
                                          (item: any, idx: number) => (
                                            <Link
                                              key={idx}
                                              href={item?.url || "#"}
                                              className={styles.submenuItem}
                                              title={item?.label}
                                            >
                                              {item?.label}
                                            </Link>
                                          )
                                        )}
                                      </div>
                                    );
                                  }
                                )}

                                {/* Handle legacy lists structure (fallback) */}
                                {subItem?.lists?.map(
                                  (section: any, i: number) => (
                                    <div
                                      key={i}
                                      className={styles.megamenuSection}
                                    >
                                      <Link
                                        href={section?.url || "#"}
                                        className={styles.sectionTitle}
                                        title={section?.title}
                                      >
                                        <h4>{section?.title}</h4>
                                      </Link>
                                      {section?.items?.map(
                                        (item: any, idx: number) => (
                                          <Link
                                            key={idx}
                                            href={item?.url || "#"}
                                            className={styles.submenuItem}
                                            title={item?.label}
                                          >
                                            {item?.label}
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  )
                                )}

                                {/* Handle legacy links structure (fallback) */}
                                {subItem?.links?.map((link: any, i: number) => (
                                  <Link
                                    key={i}
                                    href={link?.url || "#"}
                                    className={styles.submenuItem}
                                    title={link?.label}
                                  >
                                    {link?.label}
                                  </Link>
                                ))}
                              </div>
                            );
                          })}
                      </div>

                      {/* Second Column - Next 2 schools (Business School, Entrepreneurship) */}
                      <div className={styles.megamenuColumn}>
                        {megamenuData?.main_nav_links
                          ?.slice(3, 5)
                          ?.filter(
                            (subItem: any) =>
                              subItem?.title?.toLowerCase()?.trim() !==
                              "school of education"
                          )
                          ?.map((subItem: any) => {
                            return (
                              <div
                                key={subItem?.id}
                                className={styles.megamenuCategory}
                              >
                                {subItem?.url ? (
                                  <Link
                                    href={subItem?.url || "#"}
                                    className={styles.categoryTitle}
                                    title={subItem?.title}
                                  >
                                    <h3>{subItem?.title}</h3>
                                  </Link>
                                ) : (
                                  <h3 className={styles.categoryTitle}>
                                    {subItem?.title}
                                  </h3>
                                )}

                                {/* Handle sections (new API structure) */}
                                {subItem?.sections?.map(
                                  (section: any, i: number) => {
                                    return (
                                      <div
                                        key={i}
                                        className={styles.megamenuSection}
                                      >
                                        {section?.title && (
                                          <Link
                                            href={section?.url || "#"}
                                            className={styles.sectionTitle}
                                            title={section?.title}
                                          >
                                            <h4>{section?.title}</h4>
                                          </Link>
                                        )}
                                        {section?.items?.map(
                                          (item: any, idx: number) => (
                                            <Link
                                              key={idx}
                                              href={item?.url || "#"}
                                              className={styles.submenuItem}
                                              title={item?.label}
                                            >
                                              {item?.label}
                                            </Link>
                                          )
                                        )}
                                      </div>
                                    );
                                  }
                                )}

                                {/* Handle legacy lists structure (fallback) */}
                                {subItem?.lists?.map(
                                  (section: any, i: number) => (
                                    <div
                                      key={i}
                                      className={styles.megamenuSection}
                                    >
                                      <Link
                                        href={section?.url || "#"}
                                        className={styles.sectionTitle}
                                        title={section?.title}
                                      >
                                        <h4>{section?.title}</h4>
                                      </Link>
                                      {section?.items?.map(
                                        (item: any, idx: number) => (
                                          <Link
                                            key={idx}
                                            href={item?.url || "#"}
                                            className={styles.submenuItem}
                                            title={item?.label}
                                          >
                                            {item?.label}
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  )
                                )}

                                {/* Handle legacy links structure (fallback) */}
                                {subItem?.links?.map((link: any, i: number) => (
                                  <Link
                                    key={i}
                                    href={link?.url || "#"}
                                    className={styles.submenuItem}
                                    title={link?.label}
                                  >
                                    {link?.label}
                                  </Link>
                                ))}
                              </div>
                            );
                          })}
                      </div>

                      {/* Third Column - Remaining schools (Public Management, Education) */}
                      <div className={styles.megamenuColumn}>
                        {megamenuData?.main_nav_links
                          ?.slice(5)
                          ?.filter(
                            (subItem: any) =>
                              subItem?.title?.toLowerCase()?.trim() !==
                              "school of education"
                          )
                          ?.map((subItem: any) => {
                            const isPublicManagement =
                              subItem?.title?.toLowerCase()?.trim() ===
                              "school of public management";

                            return (
                              <div
                                key={subItem?.id}
                                className={styles.megamenuCategory}
                              >
                                {subItem?.url ? (
                                  <Link
                                    href={subItem?.url || "#"}
                                    className={styles.categoryTitle}
                                    title={subItem?.title}
                                  >
                                    <h3>{subItem?.title}</h3>
                                  </Link>
                                ) : (
                                  <h3 className={styles.categoryTitle}>
                                    {subItem?.title}
                                  </h3>
                                )}

                                {/* Handle sections (new API structure) */}
                                {subItem?.sections?.map(
                                  (section: any, i: number) => {
                                    return (
                                      <div
                                        key={i}
                                        className={styles.megamenuSection}
                                      >
                                        {section?.title && (
                                          <Link
                                            href={section?.url || "#"}
                                            className={styles.sectionTitle}
                                            title={section?.title}
                                          >
                                            <h4>{section?.title}</h4>
                                          </Link>
                                        )}
                                        {section?.items?.map(
                                          (item: any, idx: number) => (
                                            <Link
                                              key={idx}
                                              href={item?.url || "#"}
                                              className={styles.submenuItem}
                                              title={item?.label}
                                            >
                                              {item?.label}
                                            </Link>
                                          )
                                        )}
                                      </div>
                                    );
                                  }
                                )}

                                {/* Handle legacy lists structure (fallback) */}
                                {subItem?.lists?.map(
                                  (section: any, i: number) => (
                                    <div
                                      key={i}
                                      className={styles.megamenuSection}
                                    >
                                      <Link
                                        href={section?.url || "#"}
                                        className={styles.sectionTitle}
                                        title={section?.title}
                                      >
                                        <h4>{section?.title}</h4>
                                      </Link>
                                      {section?.items?.map(
                                        (item: any, idx: number) => (
                                          <Link
                                            key={idx}
                                            href={item?.url || "#"}
                                            className={styles.submenuItem}
                                            title={item?.label}
                                          >
                                            {item?.label}
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  )
                                )}

                                {/* Handle legacy links structure (fallback) */}
                                {subItem?.links?.map((link: any, i: number) => (
                                  <Link
                                    key={i}
                                    href={link?.url || "#"}
                                    className={styles.submenuItem}
                                    title={link?.label}
                                  >
                                    {link?.label}
                                  </Link>
                                ))}

                                {/* Special handling for School of Education when School of Public Management is present */}
                                {isPublicManagement &&
                                  megamenuData?.main_nav_links
                                    ?.filter(
                                      (n: any) =>
                                        n?.title?.toLowerCase()?.trim() ===
                                        "school of education"
                                    )
                                    ?.map((eduBlock: any) => (
                                      <div
                                        key={eduBlock?.id}
                                        className={styles.megamenuCategory}
                                        style={{ marginTop: 16 }}
                                      >
                                        <Link
                                          href={eduBlock?.url || "#"}
                                          title={eduBlock?.title}
                                          className={styles.categoryTitle}
                                        >
                                          <h3>{eduBlock?.title}</h3>
                                        </Link>

                                        {/* Handle sections for School of Education */}
                                        {eduBlock?.sections?.map(
                                          (
                                            section: any,
                                            sectionIndex: number
                                          ) => (
                                            <div
                                              key={sectionIndex}
                                              className={styles.megamenuSection}
                                            >
                                              {section?.title && (
                                                <Link
                                                  href={section?.url || "#"}
                                                  className={
                                                    styles.sectionTitle
                                                  }
                                                  title={section?.title}
                                                >
                                                  <h4>{section?.title}</h4>
                                                </Link>
                                              )}
                                              {section?.items?.map(
                                                (
                                                  item: any,
                                                  itemIndex: number
                                                ) => (
                                                  <Link
                                                    key={itemIndex}
                                                    href={item?.url || "#"}
                                                    className={
                                                      styles.submenuItem
                                                    }
                                                    title={item?.label}
                                                  >
                                                    {item?.label}
                                                  </Link>
                                                )
                                              )}
                                            </div>
                                          )
                                        )}

                                        {/* Handle legacy lists for School of Education */}
                                        {eduBlock?.lists?.map(
                                          (
                                            section: any,
                                            sectionIndex: number
                                          ) => (
                                            <div
                                              key={sectionIndex}
                                              className={styles.megamenuSection}
                                            >
                                              <Link
                                                href={section?.url || "#"}
                                                className={styles.sectionTitle}
                                                title={section?.title}
                                              >
                                                <h4>{section?.title}</h4>
                                              </Link>
                                              {section?.items?.map(
                                                (
                                                  item: any,
                                                  itemIndex: number
                                                ) => (
                                                  <Link
                                                    key={itemIndex}
                                                    href={item?.url || "#"}
                                                    className={
                                                      styles.submenuItem
                                                    }
                                                    title={item?.label}
                                                  >
                                                    {item?.label}
                                                  </Link>
                                                )
                                              )}
                                            </div>
                                          )
                                        )}

                                        {/* Handle legacy links for School of Education */}
                                        {eduBlock?.links?.map(
                                          (link: any, i: number) => (
                                            <Link
                                              key={i}
                                              href={link?.url || "#"}
                                              className={styles.submenuItem}
                                              title={link?.label}
                                            >
                                              {link?.label}
                                            </Link>
                                          )
                                        )}
                                      </div>
                                    ))}

                                {/* Handle legacy lists structure (fallback) */}
                                {subItem?.lists?.map(
                                  (section: any, i: number) => (
                                    <div
                                      key={i}
                                      className={styles.megamenuSection}
                                    >
                                      <Link
                                        href={section?.url || "#"}
                                        className={styles.sectionTitle}
                                        title={section?.title}
                                      >
                                        <h4>{section?.title}</h4>
                                      </Link>
                                      {section?.items?.map(
                                        (item: any, idx: number) => (
                                          <Link
                                            key={idx}
                                            href={item?.url || "#"}
                                            className={styles.submenuItem}
                                            title={item?.label}
                                          >
                                            {item?.label}
                                          </Link>
                                        )
                                      )}
                                    </div>
                                  )
                                )}

                                {/* Handle legacy links structure (fallback) */}
                                {subItem?.links?.map((link: any, i: number) => (
                                  <Link
                                    key={i}
                                    href={link?.url || "#"}
                                    className={styles.submenuItem}
                                    title={link?.label}
                                  >
                                    {link?.label}
                                  </Link>
                                ))}
                              </div>
                            );
                          })}
                      </div>

                      {/* Fourth Column - Certification Courses */}
                      <div className={styles.megamenuColumn}>
                        {megamenuData?.certification_courses && (
                          <div className={styles.megamenuCategory}>
                            <Link
                              href={
                                megamenuData?.certification_courses?.url || "#"
                              }
                              className={styles.categoryTitle}
                              title={megamenuData?.certification_courses?.title}
                            >
                              <h3>
                                {megamenuData?.certification_courses?.title}
                              </h3>
                            </Link>

                            <div className={styles.certificationGrid}>
                              {megamenuData?.certification_courses?.items?.map(
                                (item: any, idx: number) => (
                                  <Link
                                    key={idx}
                                    href={item?.url || "#"}
                                    className={styles.submenuItem}
                                    title={item?.label}
                                  >
                                    {item?.label}
                                  </Link>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <div className={styles.footerBottomRight}>
            <div className={styles.policies}>
              {data?.terms_condition_links?.map(
                (item: FooterItem, index: number) => (
                  <Link
                    key={index}
                    href={item?.url || "#"}
                    className="mx-2"
                    title={item?.label}
                    style={{
                      borderRight:
                        index < (data?.terms_condition_links?.length || 0) - 1
                          ? "2px solid grey"
                          : "none",
                      paddingRight: "10px",
                    }}
                    onClick={(e) => handleLinkClick(e, item?.url, router)}
                  >
                    {item?.label}
                  </Link>
                )
              )}
            </div>
          </div>
          <div className={styles.footerBottomLeft}>
            <p className={styles.copyright}>{data?.copy_right_text}</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
